<template>
  <div class="campaigns-performance-filter">
    <div class="row mb-4">
      <div class="col-md-3 pb-2">
        <loading-skeleton
          v-if="loading"
          class="campaigns-select-skeleton"
          type="card"
          :card="{ rows: 1 }"
        />
        <vue-opti-select-light
          v-show="show"
          ref="trafficSourceType"
          :disabled="disabled"
          class="optimizer-select"
          :options="options.trafficSourceTypes"
          unique-key="id"
          label-key="name"
          @change="$_changeTrafficSourceType"
          button-placeholder="Select Traffic Source Type"
          button-block
          v-model="models.trafficSourceType"
        >
          <template #ITEM="{ option }">
            <img class="acc-item-icon new-icon" :src="getTrafficSourceLogo(option.uniqueName)" alt="traffic-source-logo" />
            <div class="acc-item-name">
              {{ option.name }}
            </div>
          </template>
        </vue-opti-select-light>
      </div>
      <div class="col-md-3 pb-2">
        <loading-skeleton
          v-if="loading"
          class="campaigns-select-skeleton"
          type="card"
          :card="{ rows: 1 }"
        />
        <vue-opti-select-light
          v-show="show"
          ref="trafficSourceAccount"
          :disabled="disabled"
          class="optimizer-select"
          :options="options.trafficSourceAccounts"
          unique-key="id"
          label-key="name"
          option-type="checkbox"
          searchable
          :search-fields="['name']"
          @clear="$_changeTrafficSourceAccounts([])"
          @change="$_changeTrafficSourceAccounts"
          button-placeholder="All Accounts"
          button-placeholder-all-selected="All Accounts"
          :button-placeholder-multiple="({ count, suffix }) => `${count} Account${suffix}`"
          button-block
          allowClear
          v-model="models.trafficSourceAccounts"
        >
          <template #ITEM="{ option }">
            <img class="acc-item-icon new-icon" :src="getTrafficSourceLogo(option.trafficSourceType.uniqueName)" alt="traffic-source-logo" />
            <div class="acc-item-name">
              {{ option.name }}
            </div>
          </template>

          <template #HEADER_2>
            <header class="select-dropdown-actions">
              <b-btn @click="() => { models.trafficSourceAccounts.length === options.trafficSourceAccounts.length ? $refs.trafficSourceAccount.clear() : $refs.trafficSourceAccount.selectAll(); $refs.trafficSourceAccount.touched = true }" class="secondary-button select-all-btn">
                {{ models.trafficSourceAccounts.length === options.trafficSourceAccounts.length ? 'Deselect All Accounts' : 'Select All Accounts' }}
              </b-btn>
            </header>
          </template>
        </vue-opti-select-light>
      </div>
      <div class="col-md-3 pb-2">
        <loading-skeleton
          v-if="loading"
          class="campaigns-select-skeleton"
          type="card"
          :card="{ rows: 1 }"
        />
        <vue-opti-select-light
          v-show="show"
          ref="trackerAccount"
          :disabled="disabled"
          class="optimizer-select"
          :options="options.trackerAccounts"
          unique-key="id"
          label-key="name"
          option-type="checkbox"
          :searchable="true"
          :search-fields="['name']"
          @clear="$_changeTrackerAccounts"
          @change="$_changeTrackerAccounts"
          :groups="options.trackerGroups"
          button-placeholder="All Trackers"
          button-placeholder-all-selected="All Trackers"
          :button-placeholder-multiple="({ count, suffix }) => `${count} Tracker${suffix}`"
          button-block
          allowClear
          v-model="models.trackerAccounts"
        >
          <template #ITEM="{ option }">
            <img class="acc-item-icon" :src="option.uniqueName !== 'ClickFlare' ? `/sh_static/accounts/${option.uniqueName}.png` : '/sh_static/accounts/ClickFlare.svg'" />
            <div class="acc-item-name">
              {{ option.name }}
            </div>
          </template>
          <template #ITEM_0="{ option }">
            {{ option.name }}
          </template>
        </vue-opti-select-light>
      </div>
      <div class="col-md-3 pb-2">
        <loading-skeleton
          v-if="loading"
          class="campaigns-select-skeleton"
          type="card"
          :card="{ rows: 1 }"
        />
        <date-range
          v-else
          :disabled="disabled"
          :class="{ disabled }"
          :style="`${!show ? 'display: none !important' : ''}`"
          v-model="models.dateRangePicker"
          :auto-emit="true"
          @change="$_changeDateRangePicker"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSkeleton from '@sh/components/Utils/Skeleton/LoadingSkeleton.vue';
import { getTrafficSourceLogo } from '@sh/helpers';
import moment from 'moment';

const FORMAT = 'YYYY-MM-DD';

export default {
  name: 'CampaignsPerformanceFilter',
  components: {
    LoadingSkeleton,
  },
  props: {
    loading: { type: Boolean, default: false },
    show: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      rawApi: {
        trafficSourceTypes: [],
        trafficSourceAccounts: [],
      },
      maps: {
        trafficSourceAccountsByTypeId: {},
      },
      options: {
        trafficSourceTypes: [],
        trafficSourceAccounts: [],
        trackerAccounts: [],
        trackerGroups: [],
      },
      models: {
        trafficSourceType: [],
        trafficSourceAccounts: [],
        trackerAccounts: [],
        dateRangePicker: {
          startDate: moment(new Date()).format(FORMAT),
          endDate: moment(new Date()).endOf('day').format(FORMAT),
        },
      },
    };
  },
  async created() {
    const campaignsPerformanceFilter = this.$settings.campaignsPerformanceFilter.getFilter();

    /* *********** Set Date Range ************ */
    if (this.$route.query.startDate && this.$route.query.endDate) {
      this.models.dateRangePicker.startDate = this.$route.query.startDate;
      this.models.dateRangePicker.endDate = this.$route.query.endDate;
    } else if (campaignsPerformanceFilter) {
      const { dateRange } = campaignsPerformanceFilter;
      if (dateRange) {
        this.models.dateRangePicker.startDate = dateRange.startDate;
        this.models.dateRangePicker.endDate = dateRange.endDate;
        this.$router.replace({ query: { ...this.$route.query, ...{ startDate: dateRange.startDate, endDate: dateRange.endDate } } });
      }
    }

    try {
      /* *********** Get API Data *********** */
      [this.rawApi.trafficSourceTypes, this.rawApi.trafficSourceAccounts] = await Promise.all([this.$apiStore.trafficSources.types(), this.$apiStore.trafficSources.accounts()]);
    } catch (error) {
      this.$n_failNotification({ title: error.response.data.message });
    }
    if (this.rawApi.trafficSourceAccounts) {
      // Map Accounts
      this.rawApi.trafficSourceAccounts.forEach((account) => {
        if (!this.maps.trafficSourceAccountsByTypeId[account.type.id]) this.maps.trafficSourceAccountsByTypeId[account.type.id] = [];
        this.maps.trafficSourceAccountsByTypeId[account.type.id].push(account);
      });
      // Set TrafficSourceType Options (Keep order)
      this.options.trafficSourceTypes = this.rawApi.trafficSourceTypes.filter((trafficSourceType) => this.maps.trafficSourceAccountsByTypeId[trafficSourceType.id]).sort((a, b) => a.name.localeCompare(b.name));
      // Set Value
      if (this.$route.query.type) {
        // Set from URI
        let { type, accounts, trackers } = this.$route.query;
        type = parseInt(type, 10);
        accounts = accounts ? accounts.split(',').map((id) => parseInt(id, 10)) : null;
        trackers = trackers ? trackers.split(',').map((id) => parseInt(id, 10)) : null;
        this.$_updateAccounts({ trafficSourceTypeId: type });
        this.$nextTick(() => {
          this.$_updateAccounts({ trafficSourceTypeId: type });
          this.$nextTick(() => {
            if (accounts) this.$_updateAccounts({ trafficSourceAccountsIds: accounts });
            if (trackers) this.$_updateAccounts({ trackerAccountsIds: trackers });
            this.$_emit();
          });
        });
      } else if (campaignsPerformanceFilter) {
        // Set from LocalStorage UserSettings
        const { type, accounts, trackers } = campaignsPerformanceFilter;
        this.$_updateAccounts({ trafficSourceTypeId: type });
        this.$nextTick(() => {
          this.$_updateAccounts({ trafficSourceTypeId: type });
          this.$nextTick(() => {
            if (accounts) this.$_updateAccounts({ trafficSourceAccountsIds: accounts });
            if (trackers) this.$_updateAccounts({ trackerAccountsIds: trackers });
            this.$_emit();
          });
        });
      } else {
        // Select Default
        this.$nextTick(() => {
          this.$_updateAccounts({ trafficSourceTypeId: this.options.trafficSourceTypes[0].id });
          this.$_emit();
        });
      }
    } else {
      this.$emit('noData');
    }
  },
  methods: {
    getTrafficSourceLogo,
    $_updateAccounts({ trafficSourceTypeId, trafficSourceAccountsIds, trackerAccountsIds }, force = false) {
      let updateTrackerOptions = false;
      if (trafficSourceTypeId || force) {
        // Set Traffic Source Type
        this.$refs.trafficSourceType.clear();
        this.$refs.trafficSourceAccount.clear();
        this.$refs.trafficSourceType.add(trafficSourceTypeId);
        // Set Traffic Source Accounts Options
        this.options.trafficSourceAccounts = this.maps.trafficSourceAccountsByTypeId[trafficSourceTypeId]; // Resolve Accounts
        updateTrackerOptions = true;
      }
      // Set Traffic Source Accounts
      if (trafficSourceAccountsIds) {
        this.$refs.trafficSourceAccount.clear();
        trafficSourceAccountsIds.forEach((id) => {
          this.$refs.trafficSourceAccount.add(id);
        });
        updateTrackerOptions = true;
      }

      // Set Tracker Options
      if (updateTrackerOptions) {
        this.$refs.trackerAccount.clear();
        if (this.models.trafficSourceAccounts.length) {
          this.$_updateAccountsTracker(this.models.trafficSourceAccounts);
        } else {
          this.$_updateAccountsTracker(this.options.trafficSourceAccounts);
        }
      }

      // Set Tracker Accounts
      if (trackerAccountsIds) {
        this.$refs.trackerAccount.clear();
        trackerAccountsIds.forEach((id) => {
          this.$refs.trackerAccount.add(id);
        });
      }
    },
    $_updateAccountsTracker(trafficSourceAccounts) {
      const trackerAccountsMap = {};
      const trackerAccountOptions = [];
      const trackerGroupMap = {};
      trafficSourceAccounts.forEach((account) => {
        if (account.linkedTrackers) {
          account.linkedTrackers.forEach((tAccount) => {
            if (!trackerAccountsMap[tAccount.trackerTypeId]) trackerAccountsMap[tAccount.trackerTypeId] = {};
            trackerAccountsMap[tAccount.trackerTypeId][tAccount.id] = tAccount;
          });
        }
      });
      Object.values(trackerAccountsMap).forEach((obj) => {
        Object.values(obj).forEach((tAccount) => {
          if (!trackerGroupMap[tAccount.uniqueName]) trackerGroupMap[tAccount.uniqueName] = { value: tAccount.uniqueName, content: tAccount.uniqueName };
          trackerAccountOptions.push({ group: tAccount.uniqueName, ...tAccount });
        });
      });
      const id = 0;
      // Add No Tracker for clickhouse
      // if (process.env.VUE_APP_PLATFORM === 'mobile') {
      //   const mobileId = 1;
      //   trackerAccountOptions.push({ id: mobileId, name: 'ClickFlare', group: 'other' });
      // }
      // if (process.env.VUE_APP_PLATFORM === 'native' && session.getUser().trackerAccess) {
      //   trackerAccountOptions.push({ id: -1, name: 'ClickFlare', group: 'other' });
      // }
      trackerAccountOptions.push({ id, name: 'No Tracker', group: 'other' });
      this.options.trackerAccounts = trackerAccountOptions;
      this.options.trackerGroups = Object.values(trackerGroupMap);
      this.options.trackerGroups.push({ value: 'other', content: 'Other' });
    },
    $_changeTrafficSourceType(trafficSourceType) {
      this.$_updateAccounts({ trafficSourceTypeId: trafficSourceType[0].id }, true);
      this.$_updateQuery();
      this.$_emit(true);
    },
    $_changeTrafficSourceAccounts(trafficSourceAccounts) {
      this.$_updateAccounts({ trafficSourceAccountsIds: trafficSourceAccounts.map(({ id }) => id) });
      this.$_updateQuery();
      this.$_emit();
    },
    $_changeTrackerAccounts() {
      this.$_updateQuery();
      this.$_emit();
    },
    $_changeDateRangePicker() {
      this.$_updateQuery();
      this.$_emit();
    },
    $_emit(updateSearch = false) {
      this.$emit('input', {
        type: this.models.trafficSourceType[0],
        accounts: this.models.trafficSourceAccounts,
        trackers: this.models.trackerAccounts,
        dateRange: this.models.dateRangePicker,
      }, updateSearch);
      // Set LocalStorage UserSettings
      const payload = {
        type: this.models.trafficSourceType[0].id,
        dateRange: this.models.dateRangePicker,
      };
      if (this.models.trafficSourceAccounts.length) payload.accounts = this.models.trafficSourceAccounts.map(({ id }) => id);
      if (this.models.trackerAccounts.length) payload.trackers = this.models.trackerAccounts.map(({ id }) => id);
      this.$settings.campaignsPerformanceFilter.setFilter(payload);
    },
    $_updateQuery() {
      const query = {
        ...this.$route.query, type: this.models.trafficSourceType[0].id, startDate: this.models.dateRangePicker.startDate, endDate: this.models.dateRangePicker.endDate,
      };
      if (this.models.trafficSourceAccounts.length !== this.options.trafficSourceAccounts) {
        query.accounts = this.models.trafficSourceAccounts.map(({ id }) => id).join(',');
      } else {
        delete query.accounts;
      }
      if (this.models.trackerAccounts.length !== this.options.trackerAccounts) {
        query.trackers = this.models.trackerAccounts.map(({ id }) => id).join(',');
      } else {
        delete query.trackers;
      }
      this.$router.replace({ query });
    },
  },
};
</script>

<style lang="scss">
.campaigns-performance-filter {
  .optimizer-select {

    .dropdown-toggle {
      color: $black-500;
      // box-shadow: $btn-bs-md !important;
      // border: none;

      &:focus,
      &:active {
        color: $black-500;
      }
    }

    .acc-select {
      .acc-item-icon {
        width: 2.4rem;
        display: inline;
      }
      .acc-item-name {
        display: inline;
        padding-left: 10px;
      }
    }

    .select-dropdown-actions {
      padding-bottom: .5rem;
      border-bottom: .1rem solid #ececec;
    }
  }

  .optimizer-date-range {
    & > .reportrange-text {
      // box-shadow: $btn-bs-md;
      // border: none !important;
    }
  }
}
</style>
